import React, { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion, animate } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { faqs, serviceAreas } from '@/content/site-content';
import { applyHomeMetadata } from '@/lib/page-metadata';

export { faqs } from '@/content/site-content';

function useSystemTheme() {
  const [systemIsDark, setSystemIsDark] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  );
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => setSystemIsDark(event.matches);

    media.addEventListener?.('change', handleChange);
    return () => media.removeEventListener?.('change', handleChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const reduceMotion = shouldReduceMotion || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduceMotion) {
      root.classList.add('theme-transition');
    }

    const systemTheme = systemIsDark ? 'dark' : 'light';
    root.dataset.theme = systemTheme;
    root.style.colorScheme = systemTheme;

    if (!reduceMotion) {
      const transitionTimer = window.setTimeout(() => {
        root.classList.remove('theme-transition');
      }, 180);

      return () => window.clearTimeout(transitionTimer);
    }

    return undefined;
  }, [shouldReduceMotion, systemIsDark]);
}

export default function Home() {
  useSystemTheme();
  useEffect(() => {
    applyHomeMetadata();
  }, []);

  return (
    <div className="min-h-[100svh] bg-background text-foreground flex flex-col font-sans selection:bg-accent selection:text-white">
      <Nav />
      <main>
        <Hero />
        <TheOffer />
        <ServiceRequestPages />
        <Portfolio />
        <Capability />
        <ConsumerApps />
        <HowItWorks />
        <ServiceArea />
        <Faq />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function Nav() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (totalHeight > 0) {
        setScrollProgress(Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100)));
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = ['Work', 'Capability', 'Process', 'Contact'];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-header text-header-foreground">
      <nav aria-label="Primary navigation" className="relative">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-2">
          <a href="/" className="min-h-11 shrink-0 whitespace-nowrap flex items-center font-display text-xl sm:text-2xl font-bold tracking-tight text-header-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            ATLAS AG
          </a>
          
          <div className="flex min-w-0 items-center gap-2 sm:gap-5 lg:gap-8">
            <div className="hidden md:flex items-center gap-2 lg:gap-5">
              {links.map(link => (
                <a 
                  key={link} 
                  href={`#${link.toLowerCase()}`} 
                  className="min-h-11 flex items-center font-mono text-xs font-medium tracking-widest uppercase text-header-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent relative group"
                >
                  {link}
                  <span className="absolute bottom-1 left-0 w-0 h-[2px] bg-accent group-hover:w-full group-focus-visible:w-full transition-all duration-300 ease-out" />
                </a>
              ))}
            </div>

            <div className="min-h-11 shrink-0 flex items-center gap-1.5 font-mono text-[8px] sm:text-[10px] text-header-foreground opacity-80 whitespace-nowrap" aria-label="Accepting work in Q3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse motion-reduce:animate-none" />
              <span className="tracking-[0.12em] sm:tracking-widest">ACCEPTING WORK &mdash; Q3</span>
            </div>

            <button
              type="button"
              className="md:hidden min-h-11 min-w-11 flex items-center justify-center border border-header-foreground/35 px-2 font-mono text-[10px] tracking-widest text-header-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              MENU
            </button>
          </div>
        </div>

        <div id="mobile-navigation" className={`${mobileMenuOpen ? 'grid' : 'hidden'} md:hidden grid-cols-2 gap-px border-y border-header-foreground/20 bg-header-foreground/20`}>
          {links.map(link => (
            <a
              key={link}
              href={`#${link.toLowerCase()}`}
              onClick={() => setMobileMenuOpen(false)}
              className="min-h-11 flex items-center bg-header px-4 font-mono text-xs tracking-widest uppercase text-header-foreground hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {link}
            </a>
          ))}
        </div>
        <div 
          className="absolute bottom-0 left-0 h-[1px] bg-accent" 
          style={{ width: `${scrollProgress}%` }}
        />
      </nav>
    </header>
  );
}

function Hero() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="relative min-h-[100svh] bg-hero text-hero-foreground pt-24 sm:pt-28 pb-10 sm:pb-12 flex flex-col justify-between border-b-[4px] border-accent overflow-hidden">
      {/* Grid background */}
      <motion.div 
        className="absolute inset-0 bg-grid-pattern pointer-events-none"
        initial={shouldReduceMotion ? { clipPath: 'inset(0 0 0 0)' } : { clipPath: 'inset(0 100% 0 0)' }}
        animate={{ clipPath: 'inset(0 0 0 0)' }}
        transition={{ duration: 1, ease: 'easeOut' }}
      />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex flex-col justify-center mb-12">
        <div className="max-w-5xl">
          <h1 className="max-w-none sm:max-w-5xl font-display text-[clamp(2.7rem,13.5vw,8rem)] tracking-[-0.03em] leading-[0.87] mb-8 uppercase">
            <motion.div
              className="hero-boot-line"
              initial={shouldReduceMotion ? { clipPath: 'inset(0 0 0 0)' } : { clipPath: 'inset(0 100% 0 0)' }}
              animate={{ clipPath: 'inset(0 0 0 0)' }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              Most software goes<br className="hidden sm:block" /> looking for a problem.
            </motion.div>
            <motion.div
              className="hero-boot-line text-accent mt-2 sm:mt-4"
              initial={shouldReduceMotion ? { clipPath: 'inset(0 0 0 0)' } : { clipPath: 'inset(0 100% 0 0)' }}
              animate={{ clipPath: 'inset(0 0 0 0)' }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
            >
              These started with one.
            </motion.div>
          </h1>
          <motion.p 
            className="text-hero-muted text-xl sm:text-2xl mb-12 max-w-2xl leading-relaxed"
            initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1 }}
          >
            <span className="font-mono text-lg sm:text-xl relative -top-[1px]">14</span> products in production. Built for the work, not the demo.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.5 }}
          >
            <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white font-mono uppercase tracking-widest text-xs h-14 px-8 rounded-none transition-colors border-0" asChild>
              <a href="#contact">Start a build</a>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto border-hero-muted text-hero-foreground hover:bg-hero-foreground hover:text-hero font-mono uppercase tracking-widest text-xs h-14 px-8 rounded-none transition-colors bg-transparent" asChild>
              <a href="#work">See what we've shipped</a>
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 sm:gap-x-8 gap-y-8 sm:gap-y-12">
          <Readout end={14} label="PRODUCTS SHIPPED" delay={1.0} suffix="" />
          <Readout end={10} startDisplay={6} label="WEEK BUILD" delay={1.1} prefix="" />
          <Readout end={3} label="REGULATED INDUSTRIES" delay={1.2} suffix="" />
          <Readout end={100} label="CODE OWNERSHIP" delay={1.3} suffix="%" />
        </div>
      </div>
    </section>
  );
}

function Readout({ end, label, delay, suffix = "", startDisplay }: any) {
  const shouldReduceMotion = useReducedMotion();
  const [value, setValue] = useState(shouldReduceMotion ? end : 0);
  const [completed, setCompleted] = useState(shouldReduceMotion ? true : false);

  useEffect(() => {
    if (shouldReduceMotion) return;
    
    const timeout = setTimeout(() => {
      const controls = animate(0, end, {
        duration: 0.5,
        ease: "easeOut",
        onUpdate: (v) => setValue(Math.round(v)),
        onComplete: () => setCompleted(true)
      });
      return () => controls.stop();
    }, delay * 1000);

    return () => clearTimeout(timeout);
  }, [end, delay, shouldReduceMotion]);

  const displayValue = startDisplay && completed ? `${startDisplay}-${end}` : value;

  return (
    <div className="relative pb-4 flex flex-col justify-end min-w-0">
        <div className="font-mono text-3xl sm:text-4xl text-hero-foreground font-light tracking-tighter mb-2 h-10 flex items-end leading-none">
        {startDisplay && !completed && value > startDisplay ? `${startDisplay}-${value}` : displayValue}{suffix}
      </div>
      <div className="font-mono text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-widest text-hero-muted uppercase min-h-10 leading-relaxed">
        {label}
      </div>
      <motion.div 
        className="absolute bottom-0 left-0 h-[2px] bg-accent"
        initial={shouldReduceMotion ? { width: "100%" } : { width: "0%" }}
        animate={completed ? { width: "100%" } : { width: "0%" }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />
    </div>
  )
}

function TheOffer() {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 border-b border-border bg-background">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-[1fr_minmax(auto,600px)] gap-16 items-start mb-24">
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase">
            We build the software you couldn't buy.
          </h2>
          <div className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground pt-2">
            <p>Every product below started the same way: someone was running a real operation on spreadsheets, paper, and memory, and the tools on the market were built by people who had never done the work. We build custom platforms for businesses whose software doesn't fit them. Scheduling, tracking, quoting, compliance, field operations, customer portals, internal tools, and native mobile apps for iOS and Android. Discovery to production in weeks.</p>
            <p className="mt-5">Native mobile when the work happens away from a desk, including in-person card payments through tap to pay on the phone itself.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-10">
          {[
            {
              title: 'Built to fit',
              desc: 'No template, no forced workflow. The software matches how the operation already runs.',
              num: '01'
            },
            {
              title: 'Shipped fast',
              desc: <>Small team, modern stack, no committee. Most builds go live in <span className="font-mono text-sm relative -top-[1px]">6–10</span> weeks.</>,
              num: '02'
            },
            {
              title: 'Owned outright',
              desc: 'Your code, your database, your customers. No per-seat tax on your own operation.',
              num: '03'
            },
          ].map((item, i) => (
            <div key={i} className="group relative border border-card-border bg-card p-8 sm:p-10 flex flex-col min-h-[300px]">
              <div className="font-mono text-xs text-muted-foreground mb-12">{item.num}</div>
              <div className="mt-auto">
                <h3 className="font-display text-3xl sm:text-4xl tracking-tighter uppercase mb-4 text-primary">{item.title}</h3>
                <p className="text-muted-foreground text-[17px] leading-[1.65]">{item.desc}</p>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] bg-accent w-0 group-hover:w-full transition-all duration-300 ease-out" />
            </div>
          ))}
        </div>

        <p className="max-w-4xl border-l-2 border-accent pl-5 text-[17px] sm:text-lg leading-[1.65] text-foreground mb-12">
          Every build is priced to its own size and scope — a single-workflow tool and a multi-role platform are different projects and quoted as such. Fixed scope, fixed price, fixed date, agreed before work starts. Builds start at $15,000.
        </p>

        <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono uppercase tracking-widest text-xs h-14 px-8 rounded-none border-0" asChild>
          <a href="#contact">Tell us what you need</a>
        </Button>
      </div>
    </section>
  );
}

function ServiceRequestPages() {
  const audiences = [
    {
      label: 'FOR THE CUSTOMER',
      description: 'A short, phone-friendly form in plain language. They see exactly what happens next and get a personal status link that tracks the job through to done. A deposit is charged only if they approve it themselves.',
    },
    {
      label: 'FOR THE OWNER',
      description: 'Complete requests land in email and a simple web inbox. Deposits go directly to their own Stripe account. Each job moves forward with one tap, and the customer is updated automatically.',
    },
    {
      label: 'FOR AI ASSISTANTS',
      description: 'The page carries a machine-readable description of the business — services, area, hours, and how to book — plus a secure way for an assistant to submit a request. When someone asks an AI assistant to find and book a local service, the assistant can read what the business offers and send the request. It can never charge the customer; that still requires their approval.',
    },
  ];
  const connectionOptions = [
    {
      label: 'ON YOUR OWN SUBDOMAIN',
      description: 'Your request page lives at an address like request.yourbusiness.com. Your developer adds one DNS record and points your booking buttons at it. It takes about 15 minutes.',
    },
    {
      label: 'INSIDE YOUR EXISTING SITE',
      description: "Your developer pastes one line, and your customers can always book. On most sites the form appears right on the page, resizing to fit and working on phones. On sites with a strict security policy, the same line shows a working 'Request service' button instead, and the developer gets clear instructions for enabling the inline form.",
    },
    {
      label: 'CONNECTED TO YOUR OWN FORM',
      description: 'If your developer would rather build the form themselves, they can send requests straight into our system, and you still get the inbox, deposits and customer status links.',
    },
  ];
  const pricing = [
    { label: 'SETUP', value: '$1,200' },
    { label: 'MONTHLY', value: '$99, or $990 prepaid annually' },
    { label: 'INCLUDES', value: 'hosting, request inbox, customer status links, deposit handling, ongoing upkeep' },
  ];

  return (
    <section id="service-request-pages" aria-labelledby="service-request-pages-heading" className="bg-card border-b border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="max-w-[1400px] mx-auto">
        <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
          <span className="w-8 h-px bg-muted-foreground/30" />
          SERVICE REQUEST PAGES
        </div>
        <div className="grid lg:grid-cols-[1fr_minmax(auto,600px)] gap-10 lg:gap-16 items-start mb-14 sm:mb-20">
          <h2 id="service-request-pages-heading" className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase">
            Built for both sides of the request.
          </h2>
          <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground lg:pt-2">
            Most booking forms are built to capture leads for the business. Ours are built for the business and the customer. Customers get a quick form in plain language, a clear next step, and a live status link that follows the job from received to scheduled to on the way to done. They are never charged a deposit they did not approve. Owners get complete requests — job type, photos, address, time window — in one inbox, deposits straight to their own Stripe account, and fewer 'did you get my message?' calls.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12 sm:mb-16">
          {audiences.map(({ label, description }) => (
            <div key={label} className="border-t border-card-border pt-6 sm:pt-8">
              <h3 className="font-mono text-xs tracking-widest text-primary mb-5">{label}</h3>
              <p className="text-[17px] leading-[1.65] text-muted-foreground">{description}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-card-border pt-12 sm:pt-16 mb-12 sm:mb-16">
          <div className="grid lg:grid-cols-[1fr_minmax(auto,600px)] gap-6 lg:gap-16 items-start mb-10">
            <h3 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none uppercase text-primary">
              Already have a web developer? Keep them.
            </h3>
            <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground">
              Our request system isn't a new website. It sits behind your 'Request service' button, so your developer and your site stay exactly as they are. We set it up to match your brand, and it connects in whichever way suits you.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
            {connectionOptions.map(({ label, description }) => (
              <div key={label} className="border-t border-card-border pt-6 sm:pt-8">
                <h4 className="font-mono text-xs tracking-widest text-primary mb-5">{label}</h4>
                <p className="text-[17px] leading-[1.65] text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
          <p className="max-w-4xl text-[17px] sm:text-lg leading-[1.65] text-muted-foreground">
            Whichever way you connect it, we host and maintain the request system, including the owner inbox, deposit links, customer status updates, and the setup that lets AI assistants find and book you. You don't need new software, and your customers don't need to download anything.
          </p>
        </div>

        <div className="border-t border-card-border pt-10 sm:pt-12 mb-12 sm:mb-16">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {pricing.map(({ label, value }) => (
              <div key={label} className="border-t border-card-border pt-6">
                <h3 className="font-mono text-xs tracking-widest text-primary mb-4">{label}</h3>
                <p className="font-mono text-lg sm:text-xl leading-snug text-foreground">{value}</p>
              </div>
            ))}
          </div>
          <p className="max-w-4xl text-[17px] leading-[1.65] text-muted-foreground mb-4">
            Text message notifications, integrations with existing tools such as Jobber, and custom workflows are quoted separately.
          </p>
          <p className="max-w-4xl text-[17px] leading-[1.65] text-muted-foreground">
            This is a fixed system configured for your business, separate from custom platform builds, which start at $15,000.
          </p>
        </div>

        <p className="max-w-4xl border-l-2 border-accent pl-5 text-[17px] sm:text-lg leading-[1.65] text-foreground mb-10">
          Built for any business that takes service requests — trades, cleaners, auto shops, landscapers, salons and more. Each page carries the business's own job types, questions, photo uploads, deposit options and branding.
        </p>
        <Button variant="outline" size="lg" className="h-14 rounded-none border-card-border bg-transparent px-8 font-mono text-xs uppercase tracking-widest text-primary hover:border-accent hover:bg-background focus-visible:ring-accent" asChild>
          <a href="https://demo.atlasag.co" target="_blank" rel="noopener noreferrer">See the live demo</a>
        </Button>
      </div>
    </section>
  );
}

function Portfolio() {
  const products = [
    {
      title: 'MaintainTime',
      link: 'https://maintaintime.com',
      subline: 'Equipment maintenance for operations that run on machines.',
      body: <>Service intervals by hours, mileage, or calendar. Fuel tracking, full service history, automated alerts before something breaks instead of after. The details that matter: QR codes on each machine so an operator in gloves scans instead of logging in. English and Spanish throughout. Runs as a web app on any phone — no app store install for a <span className="font-mono text-sm relative -top-[1px]">20</span>-person crew. Owner, manager, and operator roles that reflect who actually touches what.</>,
      specs: 'CONSTRUCTION · AG · LANDSCAPING · LOGGING · MUNICIPAL / $30–55/MO · REACT · EXPRESS · POSTGRES · STRIPE',
      screenshot: '/maintaintime-shot.png'
    },
    {
      title: 'WithEase',
      link: ['https://advisewithease.com', 'https://brokerwithease.com', 'https://inswithease.com'],
      subline: 'One platform, three regulated industries.',
      body: <>Client-facing operations for financial advisors, mortgage brokers, and insurance agents. Built once as a core engine, then specialized for each vertical's workflow and compliance requirements. The same architecture serving <span className="font-mono text-sm relative -top-[1px]">3</span> different regulated professions is the difference between building a product and building a platform. That's the approach we bring to client work.</>,
      specs: 'FINANCIAL SERVICES · MORTGAGE · INSURANCE',
      screenshotSegments: [
        { title: 'AdviseWithEase', src: '/withease-advise-shot.png', href: 'https://advisewithease.com' },
        { title: 'BrokerWithEase', src: '/withease-mortgage-shot.png', href: 'https://brokerwithease.com' },
        { title: 'InsureWithEase', src: '/withease-insurance-shot.png', href: 'https://inswithease.com' },
      ],
    },
    {
      title: 'Insight Advantage',
      link: 'https://insightadvantage.io',
      subline: 'Competitive intelligence for local businesses.',
      body: <>Monitors <span className="font-mono text-sm relative -top-[1px]">10</span> data sources for competitor activity and delivers a weekly AI-written digest. <span className="font-mono text-sm relative -top-[1px]">50</span> industry-specific landing pages. Built for owners who need to know what's changing around them without hiring anyone to watch it.</>,
      specs: 'LOCAL BUSINESS · MULTI-SOURCE MONITORING · AI DIGEST',
      screenshot: '/insightadvantage-shot.png'
    },
    {
      title: 'EmailBridge',
      link: 'https://emailbridge.app',
      subline: 'Custom domain email for two dollars a month.',
      body: 'Built on Cloudflare Email Routing rather than a mailbox provider, which is why it costs a fraction of the alternatives. Sends and receives from a custom domain through an existing inbox, with automated filter setup so mail sorts itself from day one. Ships with an MCP server, so AI assistants can work the inbox directly instead of a person clicking through it.',
      specs: 'CLOUDFLARE EMAIL ROUTING · SMTP RELAY · MCP SERVER · $2/MO',
      screenshot: '/emailbridge-shot.png'
    },
    {
      title: 'Invoiceable',
      link: 'https://invoiceable.app',
      subline: 'Invoicing and payments for independent service providers.',
      body: <>Estimates, invoices, and payment collection for the people who do the work themselves — contractors, trades, and solo operators who bill from the truck rather than an office. The web platform is live and in production.<br /><br />The mobile companion, currently in App Store review, turns the phone itself into a card terminal. Tap to pay, no reader, no hardware to buy or carry. Write the estimate at the jobsite, convert it to an invoice, and take the card payment on the same device before leaving the driveway. Included with the web platform at no additional cost.</>,
      specs: 'TRADES · CONTRACTORS · SOLO OPERATORS · TAP TO PAY · WEB LIVE · MOBILE IN REVIEW',
      screenshot: '/invoiceable-shot.png'
    },
    {
      title: 'Idaho Soil Health',
      link: 'https://idahosoilhealth.com',
      subline: 'Soil-first lawn care.',
      body: 'Service and city-level pages, educational content, quote capture, and an admin dashboard that manages incoming leads alongside compost test results. Built for a services business that sells estimates, not shopping carts.',
      specs: 'LEAD GENERATION · LOCAL SEO · QUOTE MANAGEMENT',
      screenshot: '/idahosoilhealth-shot.png'
    },
    {
      title: 'TV Compost',
      link: 'https://tvcompost.com',
      subline: 'Wholesale compost supply.',
      body: 'Local search and ordering for a regional compost operation, with a full admin area to change contact information and location if the need arises.',
      specs: 'WHOLESALE · LOCAL SEO · FULL ADMIN',
      screenshot: '/tvcompost-shot.png'
    }
  ];

  return (
    <section id="work" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 border-b border-border bg-background">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-20">
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            IN PRODUCTION
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase">In production</h2>
        </div>

        <div className="space-y-8 sm:space-y-12 mb-24">
          {products.map((p, i) => (
            <SpecPlate key={i} index={i + 1} {...p} />
          ))}
        </div>

        <div className="max-w-3xl border-l-4 border-accent pl-8 py-2">
          <p className="text-[17px] sm:text-lg leading-[1.65] text-primary/80 font-serif italic">
            "A note on those last two: they're the same business at two points in the chain — wholesale supply and retail application. We built both. Software works better when whoever's building it understands what happens on either side of the screen."
          </p>
        </div>
      </div>
    </section>
  );
}

function ProductScreenshot({
  title,
  src,
  href,
  aspectClass = 'aspect-[16/10]',
  width = 1600,
  height = 1000,
}: {
  title: string;
  src: string;
  href?: string;
  aspectClass?: string;
  width?: number;
  height?: number;
}) {
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [isInView, setIsInView] = useState(false);
  const figureRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry?.isIntersecting ?? false),
      { threshold: 0.2 },
    );
    observer.observe(figure);

    return () => observer.disconnect();
  }, []);

  const figure = (
    <figure
      ref={figureRef}
      className={`relative ${aspectClass} w-full overflow-hidden rounded-[2px] border border-card-border bg-hero bg-grid-pattern`}
      data-screenshot-status={loadState}
      data-testid={`screenshot-slot-${title.toLowerCase().replaceAll(' ', '-')}`}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-hero/80 px-4 text-center"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-hero-muted">
          Website preview
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-hero-foreground">
          {title}
        </span>
        <span className="h-px w-8 bg-accent" />
      </div>
      <img
        src={src}
        alt={`Screenshot of the ${title} website`}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoadState('loaded')}
        onError={() => setLoadState('error')}
        className={`absolute inset-0 h-full w-full object-cover transition-[filter,opacity] duration-200 motion-reduce:transition-none ${
          loadState === 'loaded'
            ? `opacity-100 grayscale group-hover:grayscale-0 group-focus-visible:grayscale-0 group-focus-within:grayscale-0 ${
                isInView ? 'screenshot-mobile-in-view' : ''
              }`
            : 'pointer-events-none opacity-0'
        }`}
      />
    </figure>
  );

  if (!href) return figure;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${title} website preview`}
      className="block min-w-0 flex-1 rounded-[2px] outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
    >
      {figure}
    </a>
  );
}

function ProductScreenshotRail({
  segments,
}: {
  segments: Array<{ title: string; src: string; href: string }>;
}) {
  return (
    <div className="flex w-full items-stretch gap-px" aria-label="WithEase product websites">
      {segments.map((segment) => (
        <ProductScreenshot
          key={segment.src}
          title={segment.title}
          src={segment.src}
          href={segment.href}
          aspectClass="aspect-[533/1000]"
          width={533}
          height={1000}
        />
      ))}
    </div>
  );
}

function SpecPlate({ title, link, subline, body, specs, screenshot, screenshotSegments, index }: any) {
  const isArray = Array.isArray(link);
  const mainLink = isArray ? link[0] : link;
  const shouldReduceMotion = useReducedMotion();
  
  const content = (
    <div className="relative h-full transition-transform duration-200 ease-out group-hover:-translate-y-1 group-focus-visible:-translate-y-1">
      <div className="absolute inset-0 bg-card border border-card-border pointer-events-none" />
      
      <div className="p-5 sm:p-10 relative z-10">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          <div className="order-1 w-full lg:order-2 lg:w-2/5 lg:shrink-0">
            {screenshotSegments ? (
              <ProductScreenshotRail segments={screenshotSegments} />
            ) : (
              <ProductScreenshot title={title} src={screenshot} />
            )}
          </div>
          <div className="order-2 min-w-0 flex-1 lg:order-1">
            <div className="font-mono text-xs text-muted-foreground mb-6">
              {String(index).padStart(2, '0')}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-4 mb-8">
              <h3 className="font-display text-[clamp(2.4rem,12vw,3.75rem)] text-primary tracking-tighter uppercase break-words">{title}</h3>
              {isArray ? (
                <div className="font-mono text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  {link.map((l: string, idx: number) => (
                    <a
                      key={l}
                      href={l}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-h-11 inline-flex items-center hover:text-accent focus-visible:outline-none focus-visible:text-accent relative z-20 underline underline-offset-4 decoration-muted-foreground/30 hover:decoration-accent transition-colors"
                    >
                      {idx === 0 ? 'Visit AdviseWithEase' : idx === 1 ? 'Visit BrokerWithEase' : 'Visit InsureWithEase'}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="font-mono text-xs text-muted-foreground break-words">
                  Visit {title} at {link.replace('https://', '')}
                </div>
              )}
            </div>
            <p className="text-primary font-mono text-sm sm:text-[15px] mb-6 uppercase tracking-widest">{subline}</p>
            <p className="text-muted-foreground text-[17px] leading-[1.65]">{body}</p>
          </div>
        </div>
        
        <div className="mt-8 sm:mt-10 pt-6 border-t border-card-border/50">
          <div className="font-mono text-[11px] sm:text-xs text-muted-foreground uppercase tracking-[0.12em] sm:tracking-widest whitespace-normal break-words leading-relaxed">
            {specs}
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden">
        <div className="h-full bg-accent w-16 group-hover:w-full group-focus-visible:w-full transition-all duration-300 ease-out" />
      </div>
    </div>
  );

  const motionProps = {
    initial: shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const }
  };

  if (isArray) {
    return (
      <motion.div className="group block relative no-underline outline-none" {...motionProps}>
        {content}
      </motion.div>
    );
  }

  return (
    <motion.a 
      href={mainLink} 
      target="_blank" 
      rel="noopener noreferrer" 
      aria-label={`Visit ${title}: ${subline}`}
      className="group block relative no-underline outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-background"
      {...motionProps}
    >
      {content}
    </motion.a>
  );
}

function Capability() {
  return (
    <section id="capability" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-card border-b border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-5xl">
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            CAPABILITY
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase mb-8">
            AI where it earns its place.
          </h2>
          <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground max-w-3xl">
            Summarization, classification, extraction, pricing, monitoring — AI doing a job inside the product, not a chat box bolted to the corner of a page. We've shipped it in production across monitoring platforms, marketplaces, and pricing tools, including MCP servers that let AI agents use the software directly.
          </p>
        </div>
        <div className="mt-16 pt-8 border-t border-card-border font-mono text-[11px] sm:text-xs text-muted-foreground uppercase tracking-widest break-words leading-relaxed max-w-5xl">
          SUMMARIZATION &middot; CLASSIFICATION &middot; EXTRACTION &middot; DOCUMENT PARSING &middot; PRICING MODELS &middot; MONITORING &middot; MCP SERVERS
        </div>
      </div>
    </section>
  );
}

function ConsumerApps() {
  const apps = [
    {
      name: 'RecipePro',
      linkLabel: 'RecipePro recipe app',
      description: "Builds meals from what's already in your kitchen.",
      icon: '/recipepro-icon.png',
      iconAlt: 'RecipePro app icon',
      url: 'https://recipepro.io',
    },
    {
      name: 'Invoiceable Mobile',
      linkLabel: 'Invoiceable Mobile invoicing and tap to pay app',
      description: 'Invoiceable Mobile invoicing and tap to pay app.',
      note: 'Included with Invoiceable at no additional cost',
      icon: '/invoiceable-icon.png',
      iconAlt: 'Invoiceable Mobile app icon',
      url: 'https://invoiceable.app',
    },
    {
      name: 'Lent',
      linkLabel: 'Lent borrowed item tracking app',
      description: 'Lent borrowed item tracking app.',
      note: "Currently in App Store review · keeps track of what you've lent out, who has it, and when it should come back — with photos, reminders, and follow-ups.",
      icon: '/lent-icon.png',
      iconAlt: 'Lent app icon',
      url: 'https://lentapp.com',
    },
    {
      name: 'Clurkel',
      linkLabel: 'Clurkel neighborhood help network',
      description: 'A neighborhood help network without the surveillance and complaining. The anti-NextDoor.',
      icon: '/clurkel-icon.png',
      iconAlt: 'Clurkel app icon',
      url: 'https://clurkel.com',
    },
    {
      name: 'FitFor',
      linkLabel: 'FitFor couples fitness app',
      description: 'Functional fitness built around service to each other, strengthening the relationship alongside the body.',
      icon: '/fitfor-icon.png',
      iconAlt: 'FitFor app icon',
      url: 'https://fitnessfor.app',
    }
  ];

  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-background border-b border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12">
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            IN DEVELOPMENT
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase">Consumer apps</h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          {apps.map((app, i) => (
            app.url ? (
              <a 
                key={i} 
                href={app.url} 
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${app.linkLabel ?? `${app.name}, ${app.description}`}`}
                className="group relative flex min-h-32 items-start gap-4 p-5 sm:gap-5 sm:p-6 border border-border bg-card hover:border-accent transition-colors focus-visible:outline-none focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent"
              >
                <img
                  src={app.icon}
                  alt={app.iconAlt}
                  width={56}
                  height={56}
                  loading="lazy"
                  decoding="async"
                  className="size-12 shrink-0 rounded-[24%] border border-card-border object-cover sm:size-14"
                />
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="font-display text-2xl sm:text-3xl uppercase tracking-tighter mb-2 text-primary">{app.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest leading-relaxed break-words">
                    {app.description}
                  </div>
                  {app.note && (
                    <div className="mt-3 font-mono text-[10px] text-accent uppercase tracking-wider leading-relaxed break-words">
                      {app.note}
                    </div>
                  )}
                </div>
                <div className="absolute top-0 left-0 w-0 h-[2px] bg-accent group-hover:w-full transition-all duration-300 ease-out" />
              </a>
            ) : (
              <div key={i} className="block p-8 border border-border bg-card opacity-70">
                <div className="font-display text-3xl uppercase tracking-tighter mb-4 text-primary">{app.name}</div>
                <div className="font-mono text-[11px] text-muted-foreground truncate uppercase tracking-widest">Internal</div>
              </div>
            )
          ))}
        </div>
        <div className="font-mono text-xs text-muted-foreground tracking-widest uppercase">
          Native mobile, launching through the App Store.
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: '01',
      title: 'Call',
      desc: <><span className="font-mono text-sm relative -top-[1px]">30</span> minutes. What the operation does, where the software fails it, what 'done' looks like.</>
    },
    {
      num: '02',
      title: 'Scope',
      desc: 'Fixed scope, fixed price, fixed date. No hourly billing, no open-ended discovery phase.'
    },
    {
      num: '03',
      title: 'Build',
      desc: 'Working software in front of you weekly. You use it while it\'s being built, not after.'
    },
    {
      num: '04',
      title: 'Ship',
      desc: 'Deployed, documented, handed over. Ongoing support if you want it, not if you don\'t.'
    }
  ];

  return (
    <section id="process" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-card border-b border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-20">
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            PROCESS
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase">How it works</h2>
        </div>
        
        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div key={i} className="group relative border-t border-card-border pt-8">
              <div className="font-mono text-xs text-muted-foreground mb-8">{step.num}</div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tighter uppercase mb-4 text-primary">{step.title}</h3>
              <p className="text-muted-foreground text-[17px] leading-[1.65]">{step.desc}</p>
              <div className="absolute top-[-1px] left-0 h-[2px] bg-accent w-0 group-hover:w-full transition-all duration-300 ease-out" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-background border-b border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-4xl mb-16">
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            FAQ
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase mb-8">
            The useful questions.
          </h2>
          <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground max-w-3xl">
            Direct answers about custom software, ownership, timelines, ongoing support, and where a project fits.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 border-t border-card-border">
          {faqs.map((faq, index) => (
            <article key={faq.question} className="border-b border-card-border py-8 sm:py-10 lg:pr-12 even:lg:pl-12 even:lg:border-l">
              <div className="font-mono text-xs text-muted-foreground mb-5">{String(index + 1).padStart(2, '0')}</div>
              <h3 className="font-display text-3xl sm:text-4xl text-primary tracking-tighter uppercase mb-5">{faq.question}</h3>
              <p className="text-[16px] sm:text-[17px] leading-[1.7] text-muted-foreground">{faq.answer}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceArea() {
  return (
    <section id="service-area" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-card border-b border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-16 items-start">
          <div>
            <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-muted-foreground/30" />
              Service area
            </div>
            <h2 className="font-display text-5xl sm:text-7xl tracking-tighter leading-[0.85] uppercase mb-8">
              Close to the work. Available wherever it is.
            </h2>
            <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground max-w-xl">
              Atlas AG is based in Idaho and works remotely with businesses across the country.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 border-t border-card-border">
            {serviceAreas.map((area, index) => (
              <article
                key={area.name}
                className="border-b border-card-border py-8 sm:py-10 sm:pr-10 even:sm:pl-10 even:sm:border-l"
              >
                <div className="font-mono text-xs text-muted-foreground mb-5">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="font-display text-3xl sm:text-4xl text-primary tracking-tighter uppercase mb-4">
                  {area.name}
                </h3>
                {area.description && (
                  <p className="text-[16px] sm:text-[17px] leading-[1.7] text-muted-foreground">
                    {area.description}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const res = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error('Network response was not ok');
      e.currentTarget.reset();
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[1fr_minmax(auto,600px)] gap-16 lg:gap-32">
        <div>
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-muted-foreground/30" />
            CONTACT
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85] uppercase mb-8">
            Tell us what needs to exist.
          </h2>
          <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground max-w-xl">
            If you already know what you want built, describe it. If you only know what's broken, that works too — that's usually where these start.
          </p>
        </div>

        <div className="bg-card p-8 sm:p-12 border border-card-border relative">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-accent" />
          
          {status === 'success' ? (
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="py-16 text-center text-primary space-y-6 flex flex-col items-center justify-center min-h-[400px]"
            >
              <div className="w-12 h-12 border-2 border-accent rounded-full flex items-center justify-center mb-4">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              </div>
              <div className="font-mono text-accent text-xl tracking-widest uppercase">Received</div>
              <p className="text-[17px] text-muted-foreground">We'll review your inquiry and be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="relative space-y-8">
              <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>
              <div className="space-y-3">
                <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input id="name" name="name" type="text" autoComplete="name" required className="bg-background rounded-none border-border h-14 px-4 text-base focus-visible:ring-accent font-sans" />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input id="email" name="email" type="email" autoComplete="email" required className="bg-background rounded-none border-border h-14 px-4 text-base focus-visible:ring-accent font-sans" />
              </div>

              <div className="space-y-3">
                <Label htmlFor="company" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company</Label>
                <Input id="company" name="company" type="text" autoComplete="organization" required className="bg-background rounded-none border-border h-14 px-4 text-base focus-visible:ring-accent font-sans" />
              </div>

              <div className="space-y-3">
                <Label htmlFor="problem" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">What you're trying to solve</Label>
                <Textarea 
                  id="problem" 
                  name="problem" 
                  autoComplete="off"
                  required 
                  className="bg-background rounded-none border-border min-h-[160px] p-4 text-base resize-y focus-visible:ring-accent font-sans" 
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="timeline" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Timeline</Label>
                <select 
                  id="timeline" 
                  name="timeline"
                  autoComplete="off"
                  required
                  className="flex h-14 w-full items-center justify-between rounded-none border border-border bg-background px-4 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-sans"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='1.5' stroke='%235C6A72'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19.5 8.25l-7.5 7.5-7.5-7.5' /%3E%3C/svg%3E")`, backgroundPosition: 'right 1rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.2em' }}
                >
                  <option value="">Select a timeline...</option>
                  <option value="asap">As soon as possible</option>
                  <option value="1-3">1–3 months</option>
                  <option value="3-6">3–6 months</option>
                  <option value="exploring">Just exploring</option>
                </select>
              </div>

              {status === 'error' && (
                <div className="text-destructive font-mono text-xs uppercase tracking-widest p-4 border border-destructive/20 bg-destructive/5">
                  Submission failed. Email us at <a href="mailto:hello@atlasag.co" className="underline hover:text-accent">hello@atlasag.co</a>
                </div>
              )}

              <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {status === 'loading' ? 'Submitting your inquiry.' : ''}
              </div>

              <Button 
                type="submit" 
                size="lg" 
                aria-busy={status === 'loading'}
                className="w-full bg-accent hover:bg-accent/90 text-white font-mono uppercase tracking-widest text-xs h-14 rounded-none border-0"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Transmitting...' : 'Start a build'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="font-display text-2xl tracking-tight font-bold text-primary">
          ATLAS AG
        </div>
        <div className="flex items-center gap-6 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
          <a href="https://atlasag.co" className="min-h-11 flex items-center hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent">Atlas AG website</a>
          <a href="https://linkedin.com/company/atlasag-co" target="_blank" rel="noopener noreferrer" aria-label="Atlas AG on LinkedIn" className="min-h-11 flex items-center gap-2 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent">
            <LinkedInIcon />
            <span>LinkedIn</span>
          </a>
          <a href="https://x.com/atlasagco" target="_blank" rel="noopener noreferrer" aria-label="Atlas AG on X, @atlasagco" className="min-h-11 flex items-center gap-2 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent">
            <XIcon />
            <span>X / @atlasagco</span>
          </a>
          <span>&copy; {new Date().getFullYear()} Atlas AG.</span>
        </div>
      </div>
    </footer>
  );
}

function LinkedInIcon() {
  return (
    <svg aria-hidden="true" className="size-4 shrink-0 fill-current" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V8.999h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.606 0 4.267 2.372 4.267 5.455v6.287zM5.337 7.433a2.062 2.062 0 1 1 0-4.123 2.062 2.062 0 0 1 0 4.123zM6.119 20.452H2.692V8.999h3.427v11.453zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg aria-hidden="true" className="size-4 shrink-0 fill-current" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817-5.963 6.817H1.684l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}
